import React, { useState } from 'react';

/**
 * CommentThreadDisplay - Visar kommentarstråden för en item.
 * Används inline i tabellen i BK:s vy.
 *
 * Om handleEditByggkonsultEntry skickas in visas "✏ Redigera"-knapp på BK:s
 * publicerade thread-entries, så BK kan justera tidigare svar (Slack-style).
 *
 * @param {Object} customerComments - Kundens kommentarer för denna item
 * @param {Object} byggkonsultResponses - Byggkonsultens svar för denna item
 * @param {string} displayName - Namnet på item som kommenteras
 * @param {string} commentKey - Nyckel för att referera till svaret i backend
 * @param {Function} handleEditByggkonsultEntry - (commentKey, entryIndex, newText) → spara
 */
const CommentThreadDisplay = ({
  customerComments,
  byggkonsultResponses,
  displayName,
  commentKey,
  handleEditByggkonsultEntry,
}) => {
  const [editingEntryIdx, setEditingEntryIdx] = useState(null);
  const [editingEntryText, setEditingEntryText] = useState('');
  const [savingEntry, setSavingEntry] = useState(false);

  // Kontrollera om det finns befintlig kommentar eller byggkonsultsvar
  const hasCustomerComment = Boolean(customerComments);
  const hasConsultantReply = Boolean(byggkonsultResponses);
  const hasAnyContent = hasCustomerComment || hasConsultantReply;

  if (!hasAnyContent) {
    return (
      <div className="flex flex-col gap-3">
        <label className="text-sm font-medium text-gray-700">
          Kommentar om: {displayName}
        </label>
        <div className="text-sm text-gray-500 italic">
          Ingen kommentar finns för denna rad.
        </div>
      </div>
    );
  }

  // LÄSLÄGE: Visa KOMPLETTA tråden
  const val = customerComments;
  const customerText = typeof val === 'object' ? (val?.text || '') : (val || '');
  const customerAt = (typeof val === 'object' && (val?.commentedAt || val?.at)) ? (val.commentedAt || val.at) : null;
  const customerName = (typeof val === 'object' && val?.author?.name) ? val.author.name : 'Kund';

  const consultantObj = byggkonsultResponses;
  const consText = consultantObj ? (typeof consultantObj === 'object' ? (consultantObj?.text || '') : (consultantObj || '')) : '';
  const consAt = (consultantObj && typeof consultantObj === 'object' && (consultantObj?.respondedAt || consultantObj?.at)) ? (consultantObj.respondedAt || consultantObj.at) : null;
  const consName = (consultantObj && typeof consultantObj === 'object' && consultantObj?.author?.name) ? consultantObj.author.name : 'Du';

  const custThread = Array.isArray(val?.thread) ? val.thread : [];
  const consThread = Array.isArray(consultantObj?.thread) ? consultantObj.thread : [];

  // Bygg lista med meddelanden där kundens fråga och konsultens svar har samma layout
  const custBase = customerText ? [{ by: 'customer', text: customerText, at: customerAt, author: { name: customerName } }] : [];
  const consBase = consText ? [{ by: 'consultant', text: consText, at: consAt, author: { name: consName } }] : [];

  // Om det finns trådar, använd bara dem. Annars använd grundkommentarerna.
  // Tagga consultant-entries med _consultantIndex så vi kan referera till
  // backend's byggkonsultResponses[key].thread vid edit.
  let messages = [];
  if (custThread.length > 0 || consThread.length > 0) {
    const consTagged = consThread.map((e, i) => ({ ...e, _consultantIndex: i }));
    messages = [...custThread, ...consTagged];
  } else {
    messages = [...custBase, ...consBase];
  }

  messages = messages.filter(Boolean).sort((a, b) => {
    const ta = a?.at ? new Date(a.at).getTime() : 0;
    const tb = b?.at ? new Date(b.at).getTime() : 0;
    return ta - tb;
  });

  // Dedupe på by + text (DB kan innehålla samma meddelande flera gånger med olika tidsstämplar)
  {
    const seen = new Set();
    messages = messages.filter(msg => {
      const key = `${msg.by}::${(msg.text || '').trim()}`;
      if (!msg.text || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  if (messages.length === 0) {
    return <div className="text-sm text-gray-500 italic">Ingen kommentar</div>;
  }

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-medium text-gray-700">Konversation om: {displayName}</h4>

      <div className="mt-1 space-y-3">
        {messages.map((t, i) => {
          const canEdit = t.by === 'consultant' && typeof t._consultantIndex === 'number'
            && handleEditByggkonsultEntry && commentKey && !t.draft;
          const isEditingThis = editingEntryIdx === i;

          return (
            <div key={i}>
              <div className="text-xs text-gray-500 flex items-center gap-2">
                <span className="font-medium">{t?.author?.name || (t.by === 'consultant' ? 'Du' : 'Kund')}:</span>
                {t.at && <span>{new Date(t.at).toLocaleString('sv-SE', { dateStyle: 'short', timeStyle: 'short' })}</span>}
                {t.editedAt && (
                  <span className="italic text-gray-400" title={`Redigerad ${new Date(t.editedAt).toLocaleString('sv-SE')}`}>
                    (redigerad)
                  </span>
                )}
                {canEdit && !isEditingThis && (
                  <button
                    type="button"
                    onClick={() => {
                      setEditingEntryIdx(i);
                      setEditingEntryText(t.text || '');
                    }}
                    className="ml-auto text-xs text-blue-600 hover:text-blue-800 hover:underline"
                    title="Redigera detta svar"
                  >
                    ✏ Redigera
                  </button>
                )}
              </div>
              {isEditingThis ? (
                <div className="mt-1">
                  <textarea
                    value={editingEntryText}
                    onChange={(e) => setEditingEntryText(e.target.value)}
                    rows={3}
                    className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-leadme-pink focus:border-transparent"
                    autoFocus
                  />
                  <div className="flex gap-2 mt-1.5">
                    <button
                      type="button"
                      onClick={async () => {
                        if (!editingEntryText.trim()) return;
                        setSavingEntry(true);
                        try {
                          await handleEditByggkonsultEntry(commentKey, t._consultantIndex, editingEntryText.trim());
                          setEditingEntryIdx(null);
                          setEditingEntryText('');
                        } finally {
                          setSavingEntry(false);
                        }
                      }}
                      disabled={savingEntry || !editingEntryText.trim()}
                      className="text-xs px-3 py-1 bg-leadme-pink text-white rounded hover:bg-leadme-pink-dark disabled:opacity-50"
                    >
                      {savingEntry ? 'Sparar…' : 'Spara'}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setEditingEntryIdx(null); setEditingEntryText(''); }}
                      disabled={savingEntry}
                      className="text-xs px-3 py-1 text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                    >
                      Avbryt
                    </button>
                  </div>
                </div>
              ) : (
                <div className={`text-sm mt-1 ${t.by === 'consultant' ? 'text-green-700' : 'text-gray-800'}`}>
                  {t.text}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CommentThreadDisplay;
